/**
 * @file staging-prospect.schema.ts
 * @description Este arquivo define o schema para um "Prospecto em Staging".
 * A coleção 'stagingProspects' correspondente serve como uma área de armazenamento
 * temporária (buffer/staging) para leads capturados na aplicação.
 *
 * O objetivo a longo prazo é que esta lógica seja gerenciada por um CRM dedicado.
 * Esta coleção garante a captura imediata do lead, que pode então ser processado
 * por uma Cloud Function para sincronização com sistemas externos.
 */

import { z } from 'zod';

// --- Helper Schemas & Enums ---

/**
 * Status de um prospecto na fila de processamento/staging.
 */
const StagingProspectStatus = z.enum([
  'captured',
  'convertedToUser',
  'error',
  'deleted',
  'pendingSync',
  'synced',
]);

/**
 * [NOVO] Define as origens conhecidas de um lead. Manter como enum
 * garante a consistência dos dados para futuras análises.
 * 'TRYING_SIGNUP_FORM' se refere à "tela de Tentante".
 */
const ProspectSource = z.enum([
  'TRYING_SIGNUP_FORM', // Tela "tentando engravidar"
  'INVITE_LINK',        // Veio de um link de convite
  'MARKETING_LANDING_PAGE', // Veio de uma página de campanha
]);


// --- Main StagingProspect Schema ---

/**
 * Define o schema para um Prospecto em Staging, uma entidade temporária
 * para capturar o interesse de um usuário antes do cadastro completo.
 */
const StagingProspectSchema = z.object({
  // O email capturado do usuário.
  email: z.string().email(),

  // O status do processamento deste lead.
  status: StagingProspectStatus,

  // A origem do lead, indicando onde ele foi capturado.
  source: ProspectSource,

  // Opcional: para armazenar mensagens de erro em caso de falha na sincronização.
  syncErrorMessage: z.string().optional(),
  
  // Campos de auditoria.
  createdAt: z.date(),
  updatedAt: z.date(),
  
  deletedAt: z.date().nullable(),
});


// --- Inferred TypeScript Types ---

type StagingProspect = z.infer<typeof StagingProspectSchema>;
type StagingProspectStatus = z.infer<typeof StagingProspectStatus>;
type ProspectSource = z.infer<typeof ProspectSource>;


// --- Public Exports ---

export { StagingProspectSchema, StagingProspectStatus, ProspectSource };
export type { StagingProspect };